import { Component, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AMOUNT_DECIMALS,
  AMOUNT_FORMAT,
  CapitalizePipe,
  DialogResult,
  errorToast,
  FormErrorService,
  RcSelectableOverflowDirective,
  successSaveToast,
  SupplierComplaint,
  SupplierComplaintService,
  SupplierCreditNote,
  SupplierCreditNoteItem,
  SupplierCreditNoteItemService,
  SupplierInvoice,
  SupplierInvoiceItem,
  SupplierInvoiceItemService,
} from 'shared';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  SendSupplierComplaintModalComponent
} from '../../../supplier-complaints/send-supplier-complaint-modal/send-supplier-complaint-modal.component';
import { DialogActionsComponent, DialogContentBase, DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { HttpErrorResponse } from '@angular/common/http';
import { map, Observable, take } from 'rxjs';
import {
  CellTemplateDirective,
  CheckboxColumnComponent,
  ColumnComponent,
  GridComponent,
  GridDataResult,
  GroupHeaderTemplateDirective,
  RowArgs,
  SelectionDirective,
} from '@progress/kendo-angular-grid';
import { GroupDescriptor, process } from '@progress/kendo-data-query';
import { AsyncPipe } from '@angular/common';
import { NumericTextBoxComponent, TextAreaComponent } from '@progress/kendo-angular-inputs';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { LabelComponent } from '@progress/kendo-angular-label';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';

interface ComplaintItemDraft {
  lineItem: SupplierInvoiceItem | SupplierCreditNoteItem;
  correctAmount: number | null;
  correctPurchasePrice: number | null;
  note: string | null;
  sectionLabel: string | null;
}

@Component({
  selector: 'rc-create-supplier-complaint-modal',
  imports: [
    AsyncPipe,
    CheckboxColumnComponent,
    GridComponent,
    SelectionDirective,
    CellTemplateDirective,
    ColumnComponent,
    GroupHeaderTemplateDirective,
    TranslateModule,
    NumericTextBoxComponent,
    FormsModule,
    RcSelectableOverflowDirective,
    TextAreaComponent,
    ButtonComponent,
    CapitalizePipe,
    DialogActionsComponent,
    LabelComponent,
  ],
  providers: [FormErrorService, TranslatePipe, CapitalizePipe],
  templateUrl: './create-supplier-complaint-modal.component.html',
  styleUrl: './create-supplier-complaint-modal.component.scss',
})
export class CreateSupplierComplaintModalComponent extends DialogContentBase {
  #translateService = inject(TranslateService);
  #capitalizePipe = inject(CapitalizePipe);
  #notificationService = inject(NotificationService);
  #supplierInvoiceItemService = inject(SupplierInvoiceItemService);
  #supplierCreditNoteItemService = inject(SupplierCreditNoteItemService);
  #supplierComplaintService = inject(SupplierComplaintService);
  #dialogService = inject(DialogService);
  #router = inject(Router);
  protected readonly amountFormat = AMOUNT_FORMAT;
  protected readonly amountDecimals = AMOUNT_DECIMALS;

  supplierInvoice = signal<SupplierInvoice | undefined>(undefined);
  supplierCreditNote = signal<SupplierCreditNote | undefined>(undefined);

  gridData$: Observable<GridDataResult>;
  hasMultipleSections = signal(false);
  readonly groupDescriptors: GroupDescriptor[] = [{ field: 'sectionLabel' }];

  description = '';
  selectedKeys: string[] = [];
  selectBy = ({ dataItem }: RowArgs) => (dataItem as ComplaintItemDraft).lineItem['@id'];
  #drafts: ComplaintItemDraft[] = [];

  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: 2,
  };

  constructor() {
    super(inject(DialogRef));

    effect(() => {
      const si = this.supplierInvoice();
      if (!si) return;

      this.gridData$ = this.#supplierInvoiceItemService.getForSupplierInvoiceSection(si.supplierInvoiceSections).pipe(
        map((sections) => {
          const entries = Object.entries(sections);
          const hasMultiple = entries.length > 1;
          this.hasMultipleSections.set(hasMultiple);
          const sectionLabel = this.#translateService.instant('SECTION');
          this.#drafts = entries.flatMap(([, items], index) =>
            items.map((item) => ({
              lineItem: item,
              correctAmount: item.amount,
              correctPurchasePrice: item.purchasePrice,
              note: null,
              sectionLabel: hasMultiple ? `${sectionLabel} ${index + 1}` : null,
            })),
          );
          if (hasMultiple) return process(this.#drafts, { group: this.groupDescriptors });
          return { data: this.#drafts, total: this.#drafts.length };
        }),
      );
    });

    effect(() => {
      const scn = this.supplierCreditNote();
      if (!scn) return;

      this.gridData$ = this.#supplierCreditNoteItemService.getForSupplierCreditNote(scn['@id']!).pipe(
        map((sections) => {
          const entries = Object.entries(sections);
          const hasMultiple = entries.length > 1;
          this.hasMultipleSections.set(hasMultiple);
          const sectionLabel = this.#translateService.instant('SECTION');
          this.#drafts = entries.flatMap(([, items], index) =>
            items.map((item) => ({
              lineItem: item,
              correctAmount: item.amount,
              correctPurchasePrice: item.purchasePrice,
              note: null,
              sectionLabel: hasMultiple ? `${sectionLabel} ${index + 1}` : null,
            })),
          );
          if (hasMultiple) return process(this.#drafts, { group: this.groupDescriptors });
          return { data: this.#drafts, total: this.#drafts.length };
        }),
      );
    });
  }

  createSupplierComplaint() {
    this.#create().subscribe({
      next: (result) => {
        const n = this.#notificationService.show(successSaveToast);
        if (n.content) n.content.instance.value = this.#translateService.instant('SUPPLIER_COMPLAINT');
        this.dialog.close({ isSuccessFull: true, result });
        this.#router.navigate(['purchase/supplier_complaints', result.id]);
      },
      error: (err: HttpErrorResponse) => {
        const n = this.#notificationService.show(errorToast);
        if (n.content) n.content.instance.setCurrentValue(err);
      },
    });
  }

  createAndSendSupplierComplaint() {
    this.#create().subscribe({
      next: (result) => {
        this.dialog.close({ isSuccessFull: true, result });
        const sendDialog = this.#dialogService.open({
          content: SendSupplierComplaintModalComponent,
          title: this.#capitalizePipe.transform(
            this.#translateService.instant('SEND_ENTITY', {
              entity: this.#translateService.instant('SUPPLIER_COMPLAINT'),
            }),
          ),
          width: 1200,
          height: 1200,
        });
        sendDialog.content.instance.supplierComplaint.set(result);

        sendDialog.result.subscribe((value: DialogResult) => {
          if (value.isSuccessFull) {
            this.#router.navigate(['purchase/supplier_complaints', result.id]);
          }
        });
      },
      error: (err: HttpErrorResponse) => {
        const n = this.#notificationService.show(errorToast);
        if (n.content) n.content.instance.setCurrentValue(err);
      },
    });
  }

  #create() {
    const selectedDrafts = this.#drafts.filter((d) => this.selectedKeys.includes(d.lineItem['@id']!));
    const si = this.supplierInvoice();
    const scn = this.supplierCreditNote();

    const payload: Partial<SupplierComplaint> = {
      description: this.description,
      supplierComplaintItems: selectedDrafts.map((d) => ({
        ...(si ? { supplierInvoiceItem: d.lineItem['@id'] } : { supplierCreditNoteItem: d.lineItem['@id'] }),
        correctAmount: d.correctAmount,
        correctPurchasePrice: d.correctPurchasePrice,
        note: d.note,
      })) as unknown as string[],
    };

    if (si) payload.supplierInvoice = si['@id'];
    if (scn) payload.supplierCreditNote = scn['@id'];

    return this.#supplierComplaintService.create(payload as unknown as SupplierComplaint).pipe(take(1));
  }

  close() {
    this.dialog.close();
  }
}
